// src/pages/CompanyManagement/CreateCompanyDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const CreateCompanyDialog = ({ open, onClose, onSuccess, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tenantId: ''
  });
  const [errors, setErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Company name must be at least 2 characters';
    }

    if (!formData.tenantId || formData.tenantId.trim().length < 3) {
      newErrors.tenantId = 'Tenant ID must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/companies`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onSuccess(response.data.data);
        setFormData({ name: '', tenantId: '' });
      }
    } catch (error) {
      console.error('Error creating company:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_create'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', tenantId: '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('CompanyManagement.create_company')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            {t('CompanyManagement.create_company_info')}
          </Alert>

          <TextField
            fullWidth
            label={t('CompanyManagement.company_name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || t('CompanyManagement.company_name_helper')}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('CompanyManagement.tenant_id')}
            value={formData.tenantId}
            onChange={(e) => handleChange('tenantId', e.target.value)}
            error={!!errors.tenantId}
            helperText={errors.tenantId || t('CompanyManagement.tenant_id_helper')}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('CompanyManagement.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {t('CompanyManagement.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCompanyDialog;
